/**
 * Life4Earth V2 — Issue Flags API Adapter
 *
 * Operational issue flagging from Digest / Gantt.
 * Distinct from content-moderation comment_flags (DEV-720).
 *
 * Endpoint: /api/projects/{guid}/issue-flags
 *
 * Epic: DEV-712, Task: DEV-714
 */

import { apiFetch } from "./client";

// ── Types ─────────────────────────────────────────────────────

export type IssueSeverity = "blocking" | "warning" | "informational";
export type IssueStatus = "open" | "resolved" | "dismissed";

export interface IssueFlagResponse {
  id: string;
  projectGuid: string;
  ganttTaskId: string | null;
  reportedByUserGuid: string;
  description: string;
  severity: IssueSeverity;
  status: IssueStatus;
  resolvedBy: string | null;
  resolvedAt: string | null;
  resolutionNote: string | null;
  /** Promotion tracking — populated when flag is promoted to risk or issue. */
  promotedToType: "risk" | "issue" | null;
  promotedToId: string | null;
  promotedAt: string | null;
  promotedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateIssueFlagRequest {
  ganttTaskId?: string;
  description: string;
  severity: IssueSeverity;
}

export interface ResolveIssueFlagRequest {
  /** Target status. "open" reopens a previously resolved/dismissed flag. */
  status: "resolved" | "dismissed" | "open";
  /** Optional rationale — what was done, why dismissed, why reopened. */
  resolutionNote?: string;
}

export interface PromoteIssueFlagRequest {
  /** Target entity type. */
  promoteToType: "risk" | "issue";
  /** Optional title — defaults to flag description. */
  title?: string;
  /** Category for the new risk/issue. */
  category?: string;
  /** Responsible user. */
  ownerUserGuid?: string;
  /* ── Risk-specific fields ── */
  likelihood?: number; // 1–5
  impact?: number; // 1–5
  mitigationPlan?: string;
  /* ── Issue-specific fields ── */
  severity?: string;
  resolutionPlan?: string;
  dueDate?: string;
  /** Required when promoting directly to issue (bypassing risk stage). */
  bypassRiskNote?: string;
}

// ── API Functions ─────────────────────────────────────────────

/** Fetch all issue flags for a project. */
export async function fetchIssueFlags(
  projectGuid: string
): Promise<IssueFlagResponse[]> {
  return apiFetch<IssueFlagResponse[]>(
    `/api/projects/${projectGuid}/issue-flags`
  );
}

/** Fetch open issue flags for a specific task. */
export async function fetchIssueFlagsByTask(
  projectGuid: string,
  ganttTaskId: string
): Promise<IssueFlagResponse[]> {
  return apiFetch<IssueFlagResponse[]>(
    `/api/projects/${projectGuid}/issue-flags?taskId=${encodeURIComponent(ganttTaskId)}`
  );
}

/** Create a new issue flag. */
export async function createIssueFlag(
  projectGuid: string,
  req: CreateIssueFlagRequest
): Promise<IssueFlagResponse> {
  return apiFetch<IssueFlagResponse>(
    `/api/projects/${projectGuid}/issue-flags`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(req),
    }
  );
}

/** Resolve, dismiss, or reopen an issue flag. */
export async function resolveIssueFlag(
  projectGuid: string,
  flagId: string,
  req: ResolveIssueFlagRequest
): Promise<IssueFlagResponse> {
  return apiFetch<IssueFlagResponse>(
    `/api/projects/${projectGuid}/issue-flags/${flagId}/resolve`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(req),
    }
  );
}

/** Promote a flag to a risk or issue. */
export async function promoteIssueFlag(
  projectGuid: string,
  flagId: string,
  req: PromoteIssueFlagRequest
): Promise<IssueFlagResponse> {
  return apiFetch<IssueFlagResponse>(
    `/api/projects/${projectGuid}/issue-flags/${flagId}/promote`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(req),
    }
  );
}
